import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { listarTurmas } from '../../jogadores/api/jogadores';
import type { TurmaResumo } from '../../jogadores/api/jogadores';
import { Aviso } from '../../participantes/ui/Aviso';
import type { AvisoDaTela } from '../../participantes/ui/Aviso';
import { exportarRelatorio, salvarArquivo } from '../../relatorio/api/relatorio';
import { formatarTempoDoLider, obterOperacao, proximoNumeroDeGrupo, rotuloDoGrupo } from '../api/operacao';
import type { CardDoGrupo, FaseDoCard, VisaoDaOperacao } from '../api/operacao';
import { comandarTurma, obterSituacaoDaTurma } from '../api/turma';
import type { ComandoDaTurma, SituacaoDaTurma, TurmaFinalizada } from '../api/turma';
import { AlertaOffline } from './AlertaOffline';
import { ConfirmacaoDeTurma } from './ConfirmacaoDeTurma';
import { ModalDoGrupo } from './ModalDoGrupo';
import { NovoGrupo } from './NovoGrupo';
import { RankingDaTurma } from './RankingDaTurma';
import { manterSeDisponivel, useTurmaEmUso } from '../../../shared/turmaEmUso';

const AVISOS_DA_TURMA: Record<ComandoDaTurma, string> = {
    iniciar: 'iniciada',
    finalizar: 'finalizada',
    reabrir: 'reaberta',
};

function quando(instante: string | null): string {
    if (!instante) return '';
    return new Date(instante).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function mensagemDoErro(falha: unknown, padrao: string): string {
    const corpo = falha instanceof ErroRespostaHttp ? falha.corpo : null;
    const mensagem = typeof corpo?.mensagem === 'string' && corpo.mensagem ? corpo.mensagem : padrao;
    // O jogo diz por que recusou (ex.: "grupos em outra fase: G02").
    return typeof corpo?.motivo === 'string' && corpo.motivo ? `${mensagem} (${corpo.motivo})` : mensagem;
}

const INTERVALO_MS = 15_000;

interface PainelProps {
    csrfToken?: string;
}

function Contador({ titulo, icone, valor, total }: { titulo: string; icone: ReactNode; valor: number | null; total: number }) {
    const id = `contador-${titulo.toLowerCase().replace(/\s+/g, '-')}`;
    return (
        <section className="contador-operacao" aria-labelledby={id}>
            <h3 id={id}>{icone}{titulo}</h3>
            <p><strong>{valor ?? '–'}</strong><span> / {total}</span></p>
        </section>
    );
}

const icone = (conteudo: ReactNode) => (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {conteudo}
    </svg>
);

function situacaoDaFase(fase: FaseDoCard) {
    if (fase.pontos !== null) return <span className="pontos-fase"><strong>{fase.pontos}</strong> <small>pts</small></span>;
    if (fase.estado === 'em_andamento') return <span className="fase-em-andamento">Em andamento</span>;
    if (fase.estado === 'nao_iniciada') return <span className="fase-nao-iniciada">Não iniciado</span>;
    return <span className="fase-sem-pontos">–</span>;
}

function CaixaDaFaseAtual({ grupo }: { grupo: CardDoGrupo }) {
    if (grupo.finalizado) {
        return (
            <div className="fase-atual fase-atual-finalizada">
                <div>
                    <p className="rotulo-fase-atual">FASE ATUAL</p>
                    <p className="nome-fase-atual">FINALIZADO</p>
                </div>
            </div>
        );
    }
    const atual = grupo.fase_atual;
    const tempo = atual?.minutos != null ? `Há ${atual.minutos} min` : null;
    return (
        <div className="fase-atual">
            <div>
                <p className="rotulo-fase-atual">FASE ATUAL</p>
                <p className="nome-fase-atual">{atual?.rotulo ?? 'Nenhuma fase em andamento'}</p>
            </div>
            {tempo && (
                <p className={atual?.pausada ? 'tempo-fase-atual tempo-pausado' : 'tempo-fase-atual'}>
                    <span className="ponto-tempo" aria-hidden="true">●</span>
                    <span>{atual?.pausada ? `Pausada · ${tempo}` : tempo}</span>
                </p>
            )}
        </div>
    );
}

interface CardProps {
    grupo: CardDoGrupo;
    aoVerGrupo: () => void;

    refVerGrupo: (botao: HTMLButtonElement | null) => void;
}

function CardDoGrupoNaOperacao({ grupo, aoVerGrupo, refVerGrupo }: CardProps) {
    const nome = rotuloDoGrupo(grupo);
    const tituloId = `grupo-operacao-${grupo.grupo_id}`;
    return (
        <article className="card-grupo-operacao" aria-labelledby={tituloId}>
            <h3 id={tituloId}>{nome}</h3>
            <div className="presenca-grupo">
                <p><strong>{`${grupo.online}/${grupo.participantes}`}</strong> participantes online</p>
                {grupo.online < grupo.participantes && <AlertaOffline />}
            </div>
            <CaixaDaFaseAtual grupo={grupo} />
            <ul className="fases-do-grupo">
                {grupo.fases.map((fase) => (
                    <li key={fase.fase}>
                        <span className="rotulo-da-fase">
                            {fase.rotulo}
                            {fase.tempo_ms != null && (
                                <small className="tempo-do-lider" data-testid="tempo-do-lider" title="Tempo do líder">
                                    Líder: {formatarTempoDoLider(fase.tempo_ms)}
                                </small>
                            )}
                        </span>
                        {situacaoDaFase(fase)}
                    </li>
                ))}
            </ul>
            <p className="total-ranking-grupo" data-testid="total-ranking-do-grupo">
                <span>Total no ranking de grupo</span>
                <span className="pontos-fase"><strong>{grupo.pontos_no_ranking}</strong> <small>pts</small></span>
            </p>
            <button type="button" className="ver-grupo" ref={refVerGrupo} onClick={aoVerGrupo}>
                <span>Ver grupo</span>
                {icone(<path d="M5 12h14M13 6l6 6-6 6" />)}
            </button>
        </article>
    );
}

export function OperacaoPainel({ csrfToken = '' }: PainelProps = {}) {
    const [turmas, setTurmas] = useState<TurmaResumo[]>([]);
    const [turmaId, setTurmaId] = useTurmaEmUso();
    const [visao, setVisao] = useState<VisaoDaOperacao | null>(null);
    const [erro, setErro] = useState(false);

    const turmaAtual = useRef<string | null>(null);
    const [grupoAberto, setGrupoAberto] = useState<string | null>(null);
    const grupoAbertoAgora = useRef<string | null>(null);
    const botoesVerGrupo = useRef(new Map<string, HTMLButtonElement | null>());
    const botaoNovoGrupo = useRef<HTMLButtonElement | null>(null);
    const [novoGrupoAberto, setNovoGrupoAberto] = useState(false);
    const [aviso, setAviso] = useState<AvisoDaTela | null>(null);
    const fecharAviso = useCallback(() => setAviso(null), []);
    const [exportando, setExportando] = useState(false);
    const [situacao, setSituacao] = useState<SituacaoDaTurma | null>(null);
    const [comandoAberto, setComandoAberto] = useState<ComandoDaTurma | null>(null);
    const [comandando, setComandando] = useState(false);

    const [espacoDoTitulo, setEspacoDoTitulo] = useState<HTMLElement | null>(null);
    useEffect(() => setEspacoDoTitulo(document.getElementById('acoes-da-secao')), []);

    const fecharNovoGrupo = useCallback(() => {
        setNovoGrupoAberto(false);
        botaoNovoGrupo.current?.focus();
    }, []);

    function abrirGrupo(grupoId: string) {
        grupoAbertoAgora.current = grupoId;
        setGrupoAberto(grupoId);
    }

    const fecharGrupo = useCallback(() => {
        const aberto = grupoAbertoAgora.current;
        grupoAbertoAgora.current = null;
        setGrupoAberto(null);
        if (aberto) botoesVerGrupo.current.get(aberto)?.focus();
    }, []);
    const cardAberto = visao?.grupos.find((grupo) => grupo.grupo_id === grupoAberto) ?? null;

    const carregar = useCallback(async (alvo: string) => {
        try {
            const resposta = await obterOperacao(alvo);
            if (turmaAtual.current !== alvo) return;
            setVisao(resposta);
            setErro(false);
        } catch {
            if (turmaAtual.current === alvo) setErro(true);
        }
    }, []);

    const carregarSituacao = useCallback(async (alvo: string) => {
        try {
            const resposta = await obterSituacaoDaTurma(alvo);
            if (turmaAtual.current === alvo) setSituacao(resposta);
        } catch {
            if (turmaAtual.current === alvo) setSituacao(null);
        }
    }, []);

    useEffect(() => {
        listarTurmas()
            .then((lista) => {
                // Encerrada continua na lista (para consultar e exportar), depois das ativas.
                const ordenadas = [...lista.filter((turma) => turma.ativa), ...lista.filter((turma) => !turma.ativa)];
                setTurmas(ordenadas);
                setTurmaId((atual) =>
                    manterSeDisponivel(atual, ordenadas.map((turma) => turma.id), ordenadas.find((turma) => turma.ativa)?.id ?? ordenadas[0]?.id ?? null),
                );
            })
            .catch(() => setErro(true));
    }, []);

    useEffect(() => {
        setSituacao(null);
        if (turmaId) void carregarSituacao(turmaId);
    }, [turmaId, carregarSituacao]);

    async function confirmarComando() {
        const alvo = turmaId;
        const comando = comandoAberto;
        if (!alvo || !comando || comandando) return;
        setComandando(true);
        try {
            const resposta = await comandarTurma(alvo, comando, csrfToken);
            setSituacao(resposta);
            const ativa = resposta.situacao !== 'encerrada';
            setTurmas((lista) => lista.map((turma) => (turma.id === alvo ? { ...turma, ativa } : turma)));
            let texto = `Turma ${alvo} ${AVISOS_DA_TURMA[comando]}.`;
            if (comando === 'finalizar') {
                const { fases_encerradas: fases, estacoes_finalizadas: estacoes, sessoes_encerradas: sessoes } =
                    resposta as TurmaFinalizada;
                texto = `Turma ${alvo} finalizada: ${fases + estacoes} fase(s) encerrada(s) e ${sessoes} tablet(s) desconectado(s).`;
            }
            setAviso({ tipo: 'sucesso', texto });
            void carregar(alvo);
        } catch (falha) {
            setAviso({ tipo: 'erro', texto: mensagemDoErro(falha, 'Não foi possível concluir o comando da turma. Tente novamente.') });
            void carregarSituacao(alvo);
        } finally {
            setComandando(false);
            setComandoAberto(null);
        }
    }

    useEffect(() => {
        turmaAtual.current = turmaId;
        if (!turmaId) return undefined;
        void carregar(turmaId);
        const relogio = window.setInterval(() => {
            if (document.visibilityState === 'visible') void carregar(turmaId);
        }, INTERVALO_MS);
        return () => window.clearInterval(relogio);
    }, [turmaId, carregar]);

    async function exportarDaTurma() {
        const alvo = turmaId;
        if (!alvo || exportando) return;
        setExportando(true);
        try {
            salvarArquivo(await exportarRelatorio(alvo));
            setAviso({ tipo: 'sucesso', texto: `Relatório da turma ${alvo} exportado.` });
        } catch (falha) {
            const texto = falha instanceof ErroRespostaHttp && falha.status === 404
                ? 'Turma não encontrada.'
                : 'Não foi possível exportar o relatório. Tente novamente.';
            setAviso({ tipo: 'erro', texto });
        } finally {
            setExportando(false);
        }
    }

    const exportar = (
        <button
            type="button"
            className="botao-exportar"
            disabled={!turmaId || exportando}
            aria-busy={exportando}
            onClick={() => void exportarDaTurma()}
        >
            {icone(<path d="M12 15V3M7 8l5-5 5 5M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />)}
            {exportando ? 'Exportando…' : 'Exportar Relatório'}
        </button>
    );
    const rotulos = Object.fromEntries((visao?.grupos ?? []).map((grupo) => [grupo.grupo_id, rotuloDoGrupo(grupo)]));

    return (
        <div className="operacao">
            {espacoDoTitulo ? createPortal(exportar, espacoDoTitulo) : exportar}
            <div className="barra-turma-operacao">
                <label htmlFor="turma-operacao">ID da Turma<span aria-hidden="true">:</span></label>
                <select id="turma-operacao" value={turmaId ?? ''} onChange={(e) => setTurmaId(e.target.value)}>
                    {turmas.map((turma) => (
                        <option key={turma.id} value={turma.id}>{turma.ativa ? turma.id : `${turma.id} (encerrada)`}</option>
                    ))}
                </select>
                {situacao?.situacao === 'encerrada' ? (
                    <button
                        type="button"
                        className="botao-turma botao-iniciar-turma"
                        title={situacao.encerrada_em ? `Encerrada em ${quando(situacao.encerrada_em)}` : undefined}
                        onClick={() => setComandoAberto('reabrir')}
                    >
                        Reabrir Turma
                    </button>
                ) : (
                    <button
                        type="button"
                        className="botao-turma botao-iniciar-turma"
                        disabled={situacao?.situacao !== 'nao_iniciada'}
                        title={situacao?.iniciada_em ? `Iniciada em ${quando(situacao.iniciada_em)}` : undefined}
                        onClick={() => setComandoAberto('iniciar')}
                    >
                        {situacao?.situacao === 'em_andamento' ? 'Turma iniciada' : 'Iniciar Turma'}
                    </button>
                )}
                <button
                    type="button"
                    className="botao-turma botao-finalizar-turma"
                    disabled={!situacao || situacao.situacao === 'encerrada'}
                    onClick={() => setComandoAberto('finalizar')}
                >
                    Finalizar Turma
                </button>
                {situacao?.situacao === 'encerrada' && (
                    <span className="selo-turma-encerrada">Turma encerrada: login dos tablets bloqueado</span>
                )}
            </div>

            {erro && <p className="erro-operacao">Não foi possível carregar a operação.</p>}

            {visao && (
                <>
                    <div className="contadores-operacao">
                        <Contador
                            titulo="Participantes online"
                            icone={icone(<><path d="M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M2 9a15 15 0 0 1 20 0" /><circle cx="12" cy="19.5" r=".5" /></>)}
                            valor={visao.participantes.online}
                            total={visao.participantes.total}
                        />
                        <Contador
                            titulo="Tablets conectados"
                            icone={icone(<rect x="3" y="5" width="18" height="14" rx="2" />)}
                            valor={visao.tablets.conectados}
                            total={visao.tablets.total}
                        />
                    </div>

                    <div className="cabecalho-grupos-operacao">
                        <h2 className="titulo-grupos-operacao">{`Grupos da Turma de ID ${visao.turma_id}`}</h2>
                        <button
                            type="button"
                            className="botao-novo-grupo"
                            ref={botaoNovoGrupo}
                            onClick={() => setNovoGrupoAberto(true)}
                        >
                            <span aria-hidden="true">+</span> Novo Grupo
                        </button>
                        <RankingDaTurma turmaId={visao.turma_id} csrfToken={csrfToken} aoAvisar={setAviso} />
                        <div className="alternar-visao" role="group" aria-label="Visualização dos grupos">
                            <button type="button" className="opcao-visao" aria-pressed="true">
                                {icone(<><rect x="4" y="4" width="7" height="7" /><rect x="13" y="4" width="7" height="7" /><rect x="4" y="13" width="7" height="7" /><rect x="13" y="13" width="7" height="7" /></>)}
                                Cards
                            </button>
                        </div>
                    </div>
                    {visao.grupos.length === 0 ? (
                        <p className="vazio-operacao">Esta turma ainda não tem grupos.</p>
                    ) : (
                        <div className="grade-grupos-operacao">
                            {visao.grupos.map((grupo) => (
                                <CardDoGrupoNaOperacao
                                    key={grupo.grupo_id}
                                    grupo={grupo}
                                    aoVerGrupo={() => abrirGrupo(grupo.grupo_id)}
                                    refVerGrupo={(botao) => { botoesVerGrupo.current.set(grupo.grupo_id, botao); }}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {visao && cardAberto && (
                <ModalDoGrupo
                    turmaId={visao.turma_id}
                    csrfToken={csrfToken}
                    grupo={cardAberto}
                    aoFechar={fecharGrupo}
                    aoMudarEstacao={() => void carregar(visao.turma_id)}
                    aoMudarMembros={() => void carregar(visao.turma_id)}
                    aoExcluirGrupo={() => {
                        grupoAbertoAgora.current = null;
                        setGrupoAberto(null);
                        setAviso({ tipo: 'sucesso', texto: 'Grupo excluído com sucesso.' });
                        void carregar(visao.turma_id);
                    }}
                />
            )}

            {visao && novoGrupoAberto && (
                <NovoGrupo
                    turmaId={visao.turma_id}
                    csrfToken={csrfToken}
                    numero={proximoNumeroDeGrupo(visao.grupos)}
                    rotulos={rotulos}
                    aoFechar={fecharNovoGrupo}
                    aoConflito={() => void carregar(visao.turma_id)}
                    aoCriar={(criado) => {
                        fecharNovoGrupo();
                        setAviso({ tipo: 'sucesso', texto: `${rotuloDoGrupo(criado)} criado com sucesso.` });
                        void carregar(visao.turma_id);
                    }}
                />
            )}

            {turmaId && comandoAberto && (
                <ConfirmacaoDeTurma
                    comando={comandoAberto}
                    turmaId={turmaId}
                    processando={comandando}
                    aoCancelar={() => setComandoAberto(null)}
                    aoConfirmar={() => void confirmarComando()}
                />
            )}

            {aviso && <Aviso aviso={aviso} aoFechar={fecharAviso} />}
        </div>
    );
}
